import { Badge, Input, Select, Spinner } from '@ohear/ui'
import { type FormEvent, useEffect, useState } from 'react'
import { AuthScreen } from '../auth/AuthScreen'
import { LinkCard } from '../components/LinkCard'
import { Panel, SlpButton, StatusBar } from '../components/slp'
import {
  type CreateSessionResponse,
  type RtSessionSummary,
  createSession,
  listSessions,
} from '../lib/api'
import { authClient } from '../lib/better-auth'
import '../styles/slp.css'

const FRAME_RATES = ['23.976', '24', '25', '29.97', '30']

function statusVariant(status: RtSessionSummary['status']): 'gray' | 'green' | 'red' | 'blue' {
  if (status === 'live') return 'green'
  if (status === 'cancelled') return 'red'
  if (status === 'scheduled') return 'blue'
  return 'gray'
}

/** Second-actor link gets a distinct label even though both carry role 'actor'. */
function linkLabels(links: CreateSessionResponse['links']): string[] {
  let actorN = 0
  const actorTotal = links.filter((l) => l.role === 'actor').length
  return links.map((l) => {
    if (l.role !== 'actor') return l.role[0]?.toUpperCase() + l.role.slice(1)
    actorN += 1
    return actorTotal > 1 ? `Actor ${actorN}` : 'Actor'
  })
}

/**
 * /new — host console (G1 v2.8: wide desktop dashboard, NOT a narrow form).
 * Left: Session Control panel (create + invite links). Right: full-width
 * session history table. Fixed 1180px canvas like every other page.
 */
export function NewSessionPage() {
  const authState = authClient.useSession()
  const session = authState.data
  const authReady = !authState.isPending

  // create form
  const [title, setTitle] = useState('')
  const [projectCode, setProjectCode] = useState('')
  const [fps, setFps] = useState('24')
  const [scheduledAt, setScheduledAt] = useState('')
  const [createBusy, setCreateBusy] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [created, setCreated] = useState<CreateSessionResponse | null>(null)

  // history
  const [sessions, setSessions] = useState<RtSessionSummary[] | null>(null)
  const [listError, setListError] = useState<string | null>(null)

  useEffect(() => {
    if (!session) {
      setSessions(null)
      return
    }
    let cancelled = false
    listSessions()
      .then((res) => {
        if (!cancelled) setSessions(res.sessions)
      })
      .catch((err: unknown) => {
        if (!cancelled) setListError(err instanceof Error ? err.message : 'Could not load sessions')
      })
    return () => {
      cancelled = true
    }
  }, [session, created])

  const onCreate = async (e: FormEvent) => {
    e.preventDefault()
    if (!session || !title.trim()) return
    setCreateBusy(true)
    setCreateError(null)
    try {
      const res = await createSession({
        title: title.trim(),
        ...(projectCode.trim() ? { project_code: projectCode.trim() } : {}),
        frame_rate_fps: Number(fps),
        ...(scheduledAt ? { scheduled_at: new Date(scheduledAt).toISOString() } : {}),
      })
      setCreated(res)
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Could not create the session')
    } finally {
      setCreateBusy(false)
    }
  }

  if (!authReady) {
    return (
      <div className="flex min-h-screen min-w-[1180px] items-center justify-center bg-[var(--slp-page)]">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!session) {
    return <AuthScreen />
  }

  return (
    <div className="min-h-screen min-w-[1180px] bg-[var(--slp-page)] pb-[36px]">
      <div className="mx-auto w-[1180px] py-4">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-[20px] font-semibold tracking-tight text-[var(--slp-text)]">
              OHEAR Session
            </h1>
            <p className="text-[12px] text-[var(--slp-text-dim)]">{session.user.email}</p>
          </div>
          <SlpButton variant="secondary" size="sm" onClick={() => void authClient.signOut()}>
            Sign out
          </SlpButton>
        </div>

        <div className="flex items-start gap-3">
          {/* ── left: Session Control ─────────────────────────────────── */}
          <div className="flex w-[380px] shrink-0 flex-col gap-3">
            <Panel title="Session Control">
              <form onSubmit={(e) => void onCreate(e)} className="flex flex-col gap-2.5">
                <Input
                  placeholder="Session title (e.g. EP104 ADR — Maya)"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  data-testid="rt-new-title"
                />
                <Input
                  placeholder="Project code (optional)"
                  value={projectCode}
                  onChange={(e) => setProjectCode(e.target.value)}
                />
                <Select value={fps} onChange={(e) => setFps(e.target.value)} className="w-full">
                  {FRAME_RATES.map((r) => (
                    <option key={r} value={r}>
                      {r} fps
                    </option>
                  ))}
                </Select>
                <label className="flex flex-col gap-1 text-[11px] text-[var(--slp-text-dim)]">
                  Scheduled time (optional)
                  <Input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    className="w-full"
                    aria-label="Scheduled time (optional)"
                  />
                </label>
                {createError ? <p className="text-[12px] text-[var(--amber)]">{createError}</p> : null}
                <div>
                  <SlpButton type="submit" disabled={createBusy || !title.trim()} data-testid="rt-new-create">
                    {createBusy ? 'Creating…' : 'Create Session Link'}
                  </SlpButton>
                </div>
              </form>
            </Panel>

            {created ? (
              <Panel title={`Invite Links — ${created.session.title}`}>
                <p className="mb-2 text-[11px] text-[var(--slp-text-dim)]">
                  Send each person their own link. Links expire in 7 days.
                </p>
                <div className="flex flex-col gap-2">
                  {created.links.map((link, i) => (
                    <LinkCard key={link.token} link={link} label={linkLabels(created.links)[i]} />
                  ))}
                </div>
              </Panel>
            ) : null}
          </div>

          {/* ── right: history table ──────────────────────────────────── */}
          <div className="min-w-0 flex-1">
            <Panel title="Your Sessions">
              {listError ? (
                <p className="text-[12px] text-[var(--amber)]">{listError}</p>
              ) : sessions === null ? (
                <Spinner />
              ) : sessions.length === 0 ? (
                <p className="text-[12px] text-[var(--slp-text-dim)]">No sessions yet.</p>
              ) : (
                <table className="w-full border-collapse text-[12px]">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-[var(--slp-text-dim)]">
                      <th className="py-1.5 pr-2 font-semibold">Title</th>
                      <th className="py-1.5 pr-2 font-semibold">Project</th>
                      <th className="py-1.5 pr-2 font-semibold">Status</th>
                      <th className="py-1.5 pr-2 text-right font-semibold">fps</th>
                      <th className="py-1.5 pr-2 font-semibold">Scheduled</th>
                      <th className="py-1.5 font-semibold">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s) => (
                      <tr
                        key={s.id}
                        className="border-t border-[var(--slp-border)]"
                        data-testid="rt-session-row"
                      >
                        <td className="max-w-[280px] truncate py-1.5 pr-2 text-[var(--slp-text)]">
                          {s.title}
                        </td>
                        <td className="py-1.5 pr-2 font-mono text-[11px] text-[var(--slp-text-dim)]">
                          {s.project_code ?? '—'}
                        </td>
                        <td className="py-1.5 pr-2">
                          <Badge variant={statusVariant(s.status)}>{s.status}</Badge>
                        </td>
                        <td className="py-1.5 pr-2 text-right font-mono text-[11px] text-[var(--slp-text-dim)]">
                          {s.frame_rate_fps}
                        </td>
                        <td className="py-1.5 pr-2 text-[11px] text-[var(--slp-text-dim)]">
                          {s.scheduled_at ? new Date(s.scheduled_at).toLocaleString() : '—'}
                        </td>
                        <td className="py-1.5 text-[11px] text-[var(--slp-text-dim)]">
                          {s.created_at ? new Date(s.created_at).toLocaleString() : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>
        </div>
      </div>
      <StatusBar
        event="Ready"
        role={session.user.email ?? 'host'}
        className="fixed inset-x-0 bottom-0 z-30"
      />
    </div>
  )
}
